<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-[#000000bf] z-[100] flex items-center justify-center overflow-y-auto"
    @click.self="$emit('close')"
  >
    <div
      class="relative bg-[#eef1f0] w-[calc(100%-32px)] max-w-[1153px] md:h-[710px] h-[92vh] max-h-[92vh] rounded-[20px] md:rounded-[35px] flex flex-col md:flex-row items-center justify-center overflow-hidden mx-4 md:mx-auto"
      @touchstart.passive="onTouchStart"
      @touchend.passive="onTouchEnd"
      @touchcancel="touchStartX = 0; touchStartY = 0"
    >
      <!-- Close button -->
      <button
        class="absolute top-3.5 right-3.5 md:top-[22px] md:right-[19px] cursor-pointer z-50 w-[25px] h-[25px]"
        @click="$emit('close')"
      >
        <svg
          width="25"
          height="25"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
          />
          <line
            x1="15"
            y1="9"
            x2="9"
            y2="15"
          />
          <line
            x1="9"
            y1="9"
            x2="15"
            y2="15"
          />
        </svg>
      </button>

      <div class="w-full md:w-[1089px] flex flex-col md:flex-row justify-between items-start gap-4 lg:gap-6 h-full md:h-[631px] md:mt-[35px] overflow-hidden p-3.5 sm:p-5 md:p-0">
        <!-- Mobile Stepper (Single column header) -->
        <div class="md:hidden w-full flex items-center justify-between px-1 pt-0.5 pb-2.5 mb-1 border-b border-black/10 shrink-0">
          <div class="flex items-center gap-1.5 sm:gap-2">
            <!-- Step 1 Circle -->
            <div
              class="w-6 h-6 sm:w-7 sm:h-7 rounded-full border-[1.5px] border-black flex items-center justify-center font-bold text-xs"
              :class="currentStep > 1 ? 'bg-black text-[#01d680]' : (currentStep === 1 ? 'bg-[#01d680] text-black font-extrabold' : 'bg-transparent text-black/40 border-black/40')"
            >
              <svg
                v-if="currentStep > 1"
                class="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span v-else>1</span>
            </div>

            <!-- Line 1-2 -->
            <div
              class="w-8 sm:w-12 h-[2px] transition-colors"
              :class="currentStep > 1 ? 'bg-black' : 'bg-black/20'"
            />

            <!-- Step 2 Circle -->
            <div
              class="w-6 h-6 sm:w-7 sm:h-7 rounded-full border-[1.5px] border-black flex items-center justify-center font-bold text-xs"
              :class="currentStep === 2 ? 'bg-[#01d680] text-black font-extrabold' : 'bg-transparent text-black/40 border-black/40'"
            >
              <span>2</span>
            </div>
          </div>

          <!-- Step label indicator -->
          <span class="text-xs font-bold text-[#043f42] font-raleway mr-8">
            {{ currentStep === 1 ? 'Add Program' : 'Review' }}
          </span>
        </div>

        <!-- Sidebar -->
        <div class="w-[287px] h-[573px] bg-[#01d680] rounded-[22px] hidden md:flex flex-col pt-[56px] pl-[31px] flex-shrink-0">
          <div class="flex flex-col gap-0 relative">
            <!-- Step 1 -->
            <div class="flex items-start gap-[14px] z-10 bg-[#01d680]">
              <div
                class="flex-shrink-0 w-6 h-6 rounded-full border-[1.5px] border-black flex items-center justify-center transition-colors duration-300"
                :class="currentStep > 1 ? 'bg-black' : (currentStep === 1 ? 'bg-white' : 'bg-transparent')"
              >
                <svg
                  v-if="currentStep > 1"
                  class="w-3.5 h-3.5 text-[#01d680]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                ><path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
                  d="M5 13l4 4L19 7"
                /></svg>
                <span
                  v-else
                  class="text-black font-bold text-xs"
                  :class="{'text-white': currentStep > 1}"
                >1</span>
              </div>
              <div class="flex flex-col gap-[1px]">
                <div class="text-[15px] font-bold text-black font-raleway">
                  Add Program
                </div>
                <div class="text-[10px] font-normal text-black font-raleway leading-tight">
                  Set up qualification, enroll learners, and assign templates.
                </div>
              </div>
            </div>

            <!-- Connecting Line 1 -->
            <div
              class="absolute left-3 top-6 w-0 h-[91px] border-l border-black z-0 transition-colors duration-300"
              :class="currentStep > 1 ? 'border-opacity-100' : 'border-opacity-30'"
            />

            <!-- Step 2 -->
            <div class="flex items-start gap-[14px] mt-[80px] z-10 bg-[#01d680]">
              <div
                class="flex-shrink-0 w-6 h-6 rounded-full border-[1.5px] border-black flex items-center justify-center transition-colors duration-300"
                :class="currentStep === 2 ? 'bg-white' : 'bg-transparent border-opacity-30'"
              >
                <span
                  class="text-black font-bold text-xs"
                  :class="{'opacity-30': currentStep < 2}"
                >2</span>
              </div>
              <div
                class="flex flex-col gap-[1px]"
                :class="{'opacity-30': currentStep < 2}"
              >
                <div class="text-[15px] font-bold text-black font-raleway">
                  Review Details
                </div>
                <div class="text-[10px] font-normal text-black font-raleway leading-tight">
                  Verify program setup and attach to cohort.
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Content Area -->
        <div class="w-full md:w-[773px] h-full md:h-[569px] flex flex-col relative overflow-hidden flex-1 min-h-0">
          <transition
            :name="transitionName"
            mode="out-in"
          >
            <div
              :key="currentStep"
              class="flex flex-col justify-start md:justify-center w-full md:w-[773px] h-full absolute inset-0 overflow-y-auto md:overflow-visible px-2 sm:px-4 pt-1 md:p-0"
            >
              <div class="flex flex-col gap-4 sm:gap-6 md:gap-[35px] w-full md:w-[630px] h-full max-h-full">
                <div class="flex flex-col flex-shrink-0">
                  <h1 class="text-[#696969] text-[24px] sm:text-[30px] md:text-[36px] font-bold font-raleway leading-none">
                    Add a new program
                  </h1>
                </div>

                <!-- Step 1 Content (Manage Program) -->
                <div
                  v-if="currentStep === 1"
                  class="flex flex-col gap-4 sm:gap-6 md:gap-[35px] flex-1 overflow-hidden h-full relative"
                >
                  <div class="flex flex-col flex-shrink-0">
                    <span class="text-black text-[12px] sm:text-[13px] font-normal font-raleway leading-tight">Step 1 of 2</span>
                    <h2 class="text-black text-[20px] sm:text-[24px] md:text-[29px] font-bold font-raleway leading-tight">
                      Manage Program
                    </h2>
                  </div>

                  <!-- Scrollable Container -->
                  <div
                    ref="step1ScrollContainer"
                    class="flex flex-col gap-4 md:gap-[20px] w-full md:w-[630px] pr-1 md:pr-[15px] pt-2 md:pt-[24px] flex-1 overflow-y-auto doccu-scrollbar pb-24 md:pb-[10px] relative -mt-2 md:-mt-[24px]"
                    @scroll="checkScroll"
                  >
                    <div class="flex flex-col gap-[12px] w-full relative">
                      <!-- Row 1: Dropdown -->
                      <div class="flex flex-col sm:flex-row items-stretch sm:items-start justify-between gap-3 w-full">
                        <!-- SETA Qualification Dropdown -->
                        <div
                          v-if="!cohortSetaType"
                          class="w-full sm:w-[276px] h-[45px] rounded-full border border-black bg-white relative"
                        >
                          <CustomDropdown
                            v-model="form.setaId"
                            :options="parsedSetas"
                            placeholder="SETA Type"
                          />
                        </div>
                        <div
                          v-else
                          class="w-full sm:w-[276px] h-[45px] rounded-full border border-black bg-white relative opacity-70"
                        >
                          <input
                            type="text"
                            :value="displaySetaName"
                            class="w-full bg-transparent outline-none text-[13px] font-raleway text-black px-[20px] h-full"
                            disabled
                          >
                        </div>

                        <!-- Programme Name Dropdown -->
                        <div class="w-full sm:w-[276px] h-[45px] rounded-full border border-black bg-white relative">
                          <CustomDropdown
                            v-model="form.qualificationId"
                            :options="qualifications"
                            placeholder="Program name"
                            :disabled="!form.setaId"
                          />
                        </div>
                      </div>
                      <input
                        v-if="form.qualificationId === 'custom'"
                        v-model="form.customQualification"
                        type="text"
                        placeholder="Enter custom programme name"
                        class="w-full h-[45px] rounded-full border border-black bg-white px-[16px] text-[13px] font-raleway mt-[6px]"
                      >

                      <!-- Row 2: Upload Cards -->
                      <div class="flex flex-col sm:flex-row items-stretch justify-between gap-3 w-full">
                        <!-- Student CSV Upload -->
                        <div class="w-full sm:w-[276px] rounded-[24px] md:rounded-[34px] border border-black bg-transparent flex flex-col items-center justify-between py-[14px] px-[16px] gap-[20px] min-h-[195px] h-full">
                          <div class="flex flex-col items-center gap-[20px]">
                            <div class="w-[44px] h-[45px] bg-[#f8fff8] border border-[#22ec9b] rounded-lg flex items-center justify-center">
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#01d680"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              ><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line
                                x1="16"
                                y1="13"
                                x2="8"
                                y2="13"
                              /><line
                                x1="16"
                                y1="17"
                                x2="8"
                                y2="17"
                              /><polyline points="10 9 9 9 8 9" /></svg>
                            </div>

                            <div class="flex flex-col items-center gap-[4px]">
                              <span class="text-black text-[10px] font-raleway font-normal text-center leading-[16px] tracking-wide">
                                Upload a CSV containing the list of students for this program
                              </span>
                            </div>
                          </div>

                          <div class="flex flex-col items-center gap-2 w-full h-[52px]">
                            <label class="w-full h-[32px] rounded-full bg-white border border-[#22ec9b] flex items-center justify-center gap-2 cursor-pointer hover:bg-gray-50">
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="black"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              ><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line
                                x1="12"
                                y1="3"
                                x2="12"
                                y2="15"
                              /></svg>
                              <span class="text-black text-[10px] font-raleway">Upload student list</span>
                              <input
                                type="file"
                                accept=".csv,.txt"
                                class="hidden"
                                @change="handleCsvUpload"
                              >
                            </label>
                            <div
                              class="flex flex-col items-center gap-1 w-full"
                              :class="{ 'h-[24px]': !form.csvErrors?.length }"
                            >
                              <div
                                v-if="form.csvErrors && form.csvErrors.length > 0"
                                class="text-[10px] text-red-500 font-medium text-left w-full max-h-[100px] overflow-y-auto doccu-scrollbar flex flex-col gap-1 mt-1"
                              >
                                <span
                                  v-for="(err, errIdx) in form.csvErrors"
                                  :key="errIdx"
                                >{{ err }}</span>
                              </div>
                              <span
                                v-else-if="form.csvSummary"
                                class="text-[10px] text-[#01d680] font-bold text-center truncate w-full"
                                :title="form.csvSummary"
                              >✅ {{ form.csvSummary }}</span>
                              <span
                                v-else-if="form.csvFile"
                                class="text-[10px] text-[#01d680] font-bold text-center truncate w-full"
                                :title="form.csvFile.name"
                              >{{ form.csvFile.name }}</span>
                              <div
                                v-else
                                class="flex items-center justify-center gap-1 cursor-pointer group mt-[2px]"
                                @click="isCsvFormatModalOpen = true"
                              >
                                <svg
                                  class="w-3 h-3 text-[#3ABFF8] group-hover:text-[#289bd1] transition-colors"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  stroke-width="2"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                >
                                  <circle
                                    cx="12"
                                    cy="12"
                                    r="10"
                                  />
                                  <line
                                    x1="12"
                                    y1="16"
                                    x2="12"
                                    y2="12"
                                  />
                                  <line
                                    x1="12"
                                    y1="8"
                                    x2="12.01"
                                    y2="8"
                                  />
                                </svg>
                                <span class="text-[10px] text-[#3ABFF8] font-medium font-raleway group-hover:text-[#289bd1] underline transition-colors">
                                  View spreadsheet requirements
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Agreement Upload -->
                        <div class="w-full sm:w-[276px] rounded-[24px] md:rounded-[34px] border border-black bg-transparent flex flex-col items-center justify-between py-[14px] px-[16px] gap-[20px] min-h-[195px] h-full">
                          <div class="flex flex-col items-center gap-[20px]">
                            <div class="w-[44px] h-[45px] bg-[#f8fff8] border border-[#22ec9b] rounded-lg flex items-center justify-center">
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#01d680"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              ><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line
                                x1="16"
                                y1="13"
                                x2="8"
                                y2="13"
                              /><line
                                x1="16"
                                y1="17"
                                x2="8"
                                y2="17"
                              /><polyline points="10 9 9 9 8 9" /></svg>
                            </div>

                            <div class="flex flex-col items-center gap-[4px]">
                              <span class="text-black text-[10px] font-raleway font-normal text-center leading-[16px] tracking-wide">
                                Max 10 files (5MB/file)<br>
                                Max 1000 pages per PDF<br>
                                200MB max per cohort
                              </span>
                            </div>
                          </div>

                          <div class="flex flex-col items-center gap-2 w-full h-[52px]">
                            <label class="w-[124px] h-[32px] rounded-full bg-white border border-[#22ec9b] flex items-center justify-center gap-2 cursor-pointer hover:bg-gray-50">
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="black"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              ><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line
                                x1="12"
                                y1="3"
                                x2="12"
                                y2="15"
                              /></svg>
                              <span class="text-black text-[10px] font-raleway">Upload agreement</span>
                              <input
                                type="file"
                                accept=".pdf"
                                multiple
                                class="hidden"
                                @change="handleAgreementUpload"
                              >
                            </label>
                            <span
                              v-if="form.agreementFile"
                              class="text-[10px] text-[#01d680] font-bold text-center truncate w-full h-[12px]"
                              :title="form.agreementFile.name"
                            >{{ form.agreementFile.name }}</span>
                            <span
                              v-else
                              class="h-[12px] w-full"
                            />
                          </div>
                        </div>
                      </div>

                      <!-- Row 3: Single Sponsor (Company & Email) -->
                      <div class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 w-full mt-2 md:mt-[10px]">
                        <div class="w-full sm:w-[276px] h-[45px] rounded-full border border-black bg-white flex items-center px-[20px] relative">
                          <input
                            v-model="form.sponsors[0].companyName"
                            type="text"
                            class="w-full bg-transparent outline-none text-[13px] font-raleway text-black placeholder-[#5b5c60]"
                            placeholder="Sponsor company"
                          >
                        </div>
                        <div class="w-full sm:w-[276px] h-[45px] rounded-full border border-black bg-white flex items-center px-[20px] relative">
                          <input
                            v-model="form.sponsors[0].email"
                            type="email"
                            class="w-full bg-transparent outline-none text-[13px] font-raleway text-black placeholder-[#5b5c60]"
                            placeholder="Sponsor Email"
                          >
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-show="canScrollDown"
                    class="absolute bottom-[60px] md:bottom-[20px] left-1/2 transform -translate-x-1/2 animate-bounce bg-white rounded-full p-2 shadow z-20 text-[#01d680] pointer-events-none"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>

                <!-- Step 2 Content (Review) -->
                <div
                  v-else-if="currentStep === 2"
                  class="flex flex-col gap-4 sm:gap-6 md:gap-[35px] flex-1 overflow-hidden"
                >
                  <div class="flex flex-col flex-shrink-0">
                    <span class="text-black text-[12px] sm:text-[13px] font-normal font-raleway leading-tight">Step 2 of 2</span>
                    <h2 class="text-black text-[20px] sm:text-[24px] md:text-[29px] font-bold font-raleway leading-tight">
                      Review Program
                    </h2>
                  </div>

                  <div class="flex flex-col md:flex-row gap-4 md:gap-[40px] w-full md:w-[630px] flex-1 overflow-y-auto doccu-scrollbar pb-24 md:pb-[40px]">
                    <!-- Program Details Block -->
                    <div class="flex flex-col gap-2 md:gap-[15px] flex-1 bg-white/70 md:bg-transparent p-4 md:p-0 rounded-2xl md:rounded-none">
                      <h3 class="text-[15px] md:text-[16px] font-bold font-raleway text-[#696969]">
                        Program details
                      </h3>
                      <div class="flex flex-col gap-[7px] text-[12px] font-normal font-raleway text-[#5b5c60]">
                        <p>Program name: <span class="font-bold text-black">{{ selectedQualificationName }}</span></p>
                        <p v-if="form.studentCount">
                          No. of students: <span class="font-bold text-black">{{ form.studentCount }}</span>
                        </p>
                        <p>Sponsor: <span class="font-bold text-black">{{ form.sponsors[0]?.companyName }}</span></p>
                        <p>Sponsor email: <span class="font-bold text-black">{{ form.sponsors[0]?.email }}</span></p>
                        <p>Student list: <span class="font-bold text-black">{{ form.csvFile ? 'Uploaded' : 'None' }}</span></p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- Nav Buttons (Step 1) -->
      <div
        v-if="currentStep === 1"
        class="absolute left-1/2 -translate-x-1/2 bottom-2 md:bottom-[29px] md:left-auto md:right-[44px] md:translate-x-0 flex items-center justify-center md:justify-end gap-2.5 md:gap-[20px] z-50 bg-[#eef1f0]/95 backdrop-blur-xs md:bg-transparent px-3 py-1.5 md:p-0 rounded-full md:rounded-none shadow-sm md:shadow-none"
      >
        <button
          class="flex items-center justify-center px-4 md:w-[102px] h-[32px] md:h-[44px] rounded-full bg-transparent border border-[#043f42] text-[#043f42] text-[12px] md:text-[16px] font-bold font-raleway hover:bg-[#d5dad8] transition-colors cursor-pointer"
          @click="$emit('close')"
        >
          Cancel
        </button>
        <button
          class="flex items-center justify-center px-4 md:w-[108px] h-[32px] md:h-[44px] rounded-full transition-colors text-[12px] md:text-[16px] font-bold font-raleway cursor-pointer"
          :class="isStep1Valid ? 'bg-[#01d680] text-black hover:bg-[#00c270]' : 'bg-gray-300 text-gray-500 cursor-not-allowed'"
          :disabled="!isStep1Valid"
          @click="nextStep"
        >
          Next
        </button>
      </div>

      <!-- Nav Buttons (Step 2) -->
      <div
        v-else-if="currentStep === 2"
        class="absolute left-1/2 -translate-x-1/2 bottom-2 md:bottom-[29px] md:left-auto md:right-[44px] md:translate-x-0 flex items-center justify-center md:justify-end gap-2.5 md:gap-[20px] z-50 bg-[#eef1f0]/95 backdrop-blur-xs md:bg-transparent px-3 py-1.5 md:p-0 rounded-full md:rounded-none shadow-sm md:shadow-none"
      >
        <button
          class="flex items-center justify-center px-4 md:w-[102px] h-[32px] md:h-[44px] rounded-full bg-transparent border border-[#043f42] text-[#043f42] text-[12px] md:text-[16px] font-bold font-raleway hover:bg-[#d5dad8] transition-colors cursor-pointer"
          @click="prevStep"
        >
          Back
        </button>
        <button
          class="flex items-center justify-center px-4 md:px-5 md:w-[130px] h-[32px] md:h-[44px] rounded-full transition-colors text-[12px] md:text-[16px] font-bold font-raleway whitespace-nowrap cursor-pointer"
          :class="(isStep2Valid && !isSubmitting) ? 'bg-[#01d680] text-black hover:bg-[#00c270]' : 'bg-gray-300 text-gray-500 cursor-not-allowed'"
          :disabled="!isStep2Valid || isSubmitting"
          @click="submitProgramme"
        >
          {{ isSubmitting ? 'Creating...' : 'Create program' }}
        </button>
      </div>
    </div>

    <!-- CSV Format Guide Modal -->
    <dialog
      id="csv_format_modal"
      :class="['modal', { 'modal-open': isCsvFormatModalOpen }]"
    >
      <div class="modal-box w-[calc(100%-32px)] max-w-lg mx-4 sm:mx-auto bg-white border border-[#22ec9b] rounded-[24px] p-5 sm:p-8 relative shadow-[0_8px_30px_rgb(0,0,0,0.12)]">
        <h3 class="font-bold text-[22px] text-black font-raleway mb-2">
          Student List Formatting Requirements
        </h3>
        <p class="py-2 text-[14px] text-gray-600 font-raleway leading-relaxed">
          To ensure your cohort is registered accurately, please arrange your spreadsheet exactly in the order below. Doccu processes the data by column position.
        </p>

        <div class="flex flex-col gap-4 mt-6 mb-4">
          <div class="flex flex-col bg-[#f8fff8] rounded-2xl p-4 border border-[#22ec9b]/30">
            <span class="text-[11px] font-bold text-[#01d680] uppercase tracking-wider mb-1.5">Column 1 (Required)</span>
            <span class="text-[15px] text-black font-raleway font-semibold">Student Name</span>
          </div>
          <div class="flex flex-col bg-[#f8fff8] rounded-2xl p-4 border border-[#22ec9b]/30">
            <span class="text-[11px] font-bold text-[#01d680] uppercase tracking-wider mb-1.5">Column 2 (Required)</span>
            <span class="text-[15px] text-black font-raleway font-semibold">Student Surname</span>
          </div>
          <div class="flex flex-col bg-[#f8fff8] rounded-2xl p-4 border border-[#22ec9b]/30">
            <span class="text-[11px] font-bold text-[#01d680] uppercase tracking-wider mb-1.5">Column 3 (Required)</span>
            <span class="text-[15px] text-black font-raleway font-semibold">Email Address</span>
          </div>
          <div class="flex flex-col bg-gray-50 rounded-2xl p-4 border border-gray-200">
            <span class="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Column 4 (Optional)</span>
            <span class="text-[15px] text-gray-700 font-raleway font-medium">Cell Number</span>
          </div>
        </div>

        <div class="modal-action mt-8 flex justify-end">
          <button
            class="flex items-center justify-center h-[44px] px-8 rounded-[38px] bg-[#043f42] hover:bg-[#032a2c] text-white font-bold font-raleway transition-colors border-none"
            @click="isCsvFormatModalOpen = false"
          >
            Understood
          </button>
        </div>
      </div>
      <form
        method="dialog"
        class="modal-backdrop bg-black/40"
        @click="isCsvFormatModalOpen = false"
      >
        <button>close</button>
      </form>
    </dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import setaQualificationsData from '../data/setas_and_qualifications.json'
import CustomDropdown from '../components/custom_dropdown.vue'

const props = defineProps({
  show: Boolean,
  cohortSlug: {
    type: String,
    default: ''
  },
  cohortSetaType: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['close', 'created'])

const currentStep = ref(1)
const step1ScrollContainer = ref(null)
const canScrollDown = ref(false)
const transitionName = ref('slide-right')
const isSubmitting = ref(false)
const isCsvFormatModalOpen = ref(false)

const form = ref({
  setaId: '',
  qualificationId: '',
  customQualification: '',
  csvFile: null,
  csvErrors: null,
  csvSummary: null,
  agreementFile: null,
  agreementFiles: [],
  sponsors: [{ email: '', companyName: '' }]
})

const allBodies = computed(() => {
  const setas = (setaQualificationsData?.setas || []).map(s => ({
    ...s,
    name: s.short_name || (s.name ? s.name.split(' (')[0] : s.id),
    fullName: s.name || s.id
  }))
  const qcto = (setaQualificationsData?.qcto_clusters || []).map(c => ({
    ...c,
    name: c.short_name || c.name,
    fullName: c.name
  }))
  return [...setas, ...qcto]
})

const parsedSetas = computed(() => allBodies.value)

const qualifications = computed(() => {
  const targetBody = form.value.setaId
  if (!targetBody) return []
  const body = allBodies.value.find(s => s.id === targetBody)
  const baseQuals = (body?.qualifications || []).map(q => {
    const metaParts = []
    if (q.nqf_level) metaParts.push(`NQF ${q.nqf_level}`)
    if (q.saqa_id) metaParts.push(`SAQA ${q.saqa_id}`)
    const metaStr = metaParts.length > 0 ? ` (${metaParts.join(' · ')})` : ''
    return {
      id: q.id,
      name: `${q.name}${metaStr}`,
      fullName: q.name
    }
  })
  baseQuals.push({ id: 'custom', name: '+ Add Custom Programme' })
  return baseQuals
})

const checkScroll = () => {
  if (!step1ScrollContainer.value) return
  const el = step1ScrollContainer.value
  canScrollDown.value = el.scrollHeight > el.clientHeight && Math.ceil(el.scrollTop) < el.scrollHeight - el.clientHeight - 1
}

const normalizeSetaId = (inputSeta) => {
  if (!inputSeta || inputSeta === 'SETA') return ''
  const searchStr = inputSeta.toLowerCase().replace(/seta|qcto/, '').trim()
  const found = allBodies.value.find(s =>
    s.id.toLowerCase() === inputSeta.toLowerCase() ||
    (searchStr.length > 0 && s.id.toLowerCase().includes(searchStr))
  )
  return found ? found.id : inputSeta
}

const displaySetaName = computed(() => {
  const body = allBodies.value.find(s => s.id === form.value.setaId)
  return body ? (body.short_name || body.name) : form.value.setaId.toUpperCase()
})

watch(() => props.show, (newVal) => {
  if (newVal) {
    currentStep.value = 1
    form.value = {
      setaId: normalizeSetaId(props.cohortSetaType),
      qualificationId: '',
      csvFile: null,
      csvErrors: null,
      csvSummary: null,
      agreementFile: null,
      agreementFiles: [],
      sponsors: [{ email: '', companyName: '' }]
    }
  }
})

const isStep1Valid = computed(() => {
  const hasBasic = form.value.qualificationId && form.value.qualificationId.trim() !== '' &&
                   form.value.csvFile !== null &&
                   !form.value.csvErrors &&
                   form.value.csvSummary !== null &&
                   form.value.agreementFiles && form.value.agreementFiles.length > 0

  if (!hasBasic) return false

  if (!form.value.sponsors || form.value.sponsors.length === 0) return false

  const sponsor = form.value.sponsors[0]
  return sponsor &&
         sponsor.companyName && sponsor.companyName.trim() !== '' &&
         sponsor.email && sponsor.email.trim() !== ''
})

const isStep2Valid = computed(() => {
  return isStep1Valid.value
})

const notify = (type, message) => {
  if (typeof window !== 'undefined' && window.showFlash) {
    window.showFlash(type, message)
  } else {
    console.warn(`[${type}]`, message)
  }
}

const handleCsvUpload = async (e) => {
  if (form.value.isValidating) return

  if (e.target.files && e.target.files.length > 0) {
    const file = e.target.files[0]
    if (!file.name.toLowerCase().match(/\.(csv|txt)$/)) {
      notify('error', 'Only CSV or text files are allowed')
      return
    }
    form.value.csvFile = file
    form.value.csvErrors = null
    form.value.csvSummary = null
    form.value.isValidating = true

    const formData = new FormData()
    formData.append('student_csv', file)

    try {
      const response = await fetch('/cohorts/validate_csv', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: formData
      })

      const data = await response.json()

      if (!response.ok || data.errors) {
        const errors = data.errors || ['Validation failed']
        form.value.csvErrors = errors
        form.value.csvFile = null
      } else {
        form.value.csvSummary = `${data.total_students} students uploaded`
        form.value.studentCount = data.total_students
      }
    } catch (error) {
      console.error('CSV validation error:', error)
      const err = [(error.stack || error.message || 'An error occurred during validation')]
      form.value.csvErrors = err
      form.value.csvFile = null
      if (error.message && error.message.includes('capacity')) {
        notify('error', error.message)
      }
    } finally {
      form.value.isValidating = false
    }
  } else {
    form.value.csvFile = null
    form.value.csvErrors = null
    form.value.csvSummary = null
  }
}

const handleAgreementUpload = (e) => {
  if (e.target.files && e.target.files.length > 0) {
    const files = Array.from(e.target.files)
    const existingFiles = form.value.agreementFiles || []
    if (existingFiles.length + files.length > 10) {
      notify('error', 'Max 10 files allowed')
      return
    }
    const oversized = files.find(f => f.size > 5 * 1024 * 1024)
    if (oversized) {
      notify('error', 'Max 5MB per file')
      return
    }
    form.value.agreementFile = files[files.length - 1]
    form.value.agreementFiles = [...existingFiles, ...files]
    nextTick(() => checkScroll())
  }
}

const selectedQualificationName = computed(() => {
  const qual = qualifications.value.find(q => q.id === form.value.qualificationId)
  if (!qual) return form.value.qualificationId
  return qual.name.length > 20 ? qual.name.substring(0, 20) + '...' : qual.name
})

const nextStep = () => {
  if (currentStep.value === 1 && !isStep1Valid.value) return
  if (currentStep.value < 2) {
    transitionName.value = 'slide-right'
    currentStep.value++
  }
}

const prevStep = () => {
  if (currentStep.value > 1) {
    transitionName.value = 'slide-left'
    currentStep.value--
  }
}

const touchStartX = ref(0)
const touchStartY = ref(0)

const onTouchStart = (e) => {
  if (e.target && e.target.closest('input, textarea, select, .custom-dropdown, label, button')) return
  if (e.touches && e.touches.length === 1) {
    touchStartX.value = e.touches[0].clientX
    touchStartY.value = e.touches[0].clientY
  }
}

const onTouchEnd = (e) => {
  if (!touchStartX.value || !touchStartY.value) return
  if (e.changedTouches && e.changedTouches.length === 1) {
    const deltaX = e.changedTouches[0].clientX - touchStartX.value
    const deltaY = e.changedTouches[0].clientY - touchStartY.value
    // Detect horizontal swipe with clear intent (horizontal dominance > 1.4x and distance > 45px)
    if (Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY) * 1.4) {
      if (deltaX < 0) {
        // Swipe left -> advance to Step 2 if Step 1 is practical
        if (currentStep.value === 1 && isStep1Valid.value) {
          nextStep()
        }
      } else if (deltaX > 0) {
        // Swipe right -> return to Step 1 from Step 2
        if (currentStep.value === 2) {
          prevStep()
        }
      }
    }
  }
  touchStartX.value = 0
  touchStartY.value = 0
}

const submitProgramme = async () => {
  if (!isStep2Valid.value) return
  isSubmitting.value = true

  const formData = new FormData()
  formData.append('programme[seta_type]', form.value.setaId)
  if (form.value.qualificationId === 'custom') {
    formData.append('programme[qualification_id]', form.value.customQualification)
  } else {
    formData.append('programme[qualification_id]', form.value.qualificationId)
  }

  if (form.value.csvFile) {
    formData.append('programme[student_csv]', form.value.csvFile)
  }

  if (form.value.agreementFiles && form.value.agreementFiles.length > 0) {
    form.value.agreementFiles.forEach(file => {
      formData.append('programme[agreements][]', file)
    })
  }

  form.value.sponsors.forEach((sponsor, sIdx) => {
    formData.append(`programme[sponsors_attributes][${sIdx}][name]`, sponsor.companyName)
    formData.append(`programme[sponsors_attributes][${sIdx}][email]`, sponsor.email)
    formData.append(`programme[sponsors_attributes][${sIdx}][company_name]`, sponsor.companyName)
  })

  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(`/cohorts/${props.cohortSlug}/programmes`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        Accept: 'application/json'
      },
      body: formData
    })

    if (response.ok) {
      const data = await response.json()
      emit('created', data.programme)
      emit('close')
    } else {
      const errorData = await response.json()
      notify('error', 'Error creating programme: ' + (errorData.errors ? errorData.errors.join(', ') : 'Unknown error'))
    }
  } catch (error) {
    console.error(error)
    notify('error', 'Network error')
  } finally {
    isSubmitting.value = false
  }
}
</script>
